import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  UploadCloud,
  FileSpreadsheet,
  ArrowLeft,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Package,
  Users,
  Truck,
  History,
  Save,
  ChevronRight,
  AlertCircle,
  Boxes,
  Building2
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchAllPaginated, resolvePreferredStockSiteId } from '../utils/productCatalog';
import { BILLING_BRAND } from '../data/billingBrandConfig';

// Nombre de marca por app (Impulza / Gastify). billingBrandConfig esta
// PROTECTED en el sync: este archivo SI viaja, asi que evitamos hardcodear
// "Impulza" aca para que cada app muestre su propio nombre.
const BRAND_NAME = BILLING_BRAND?.brandName || 'el sistema';

// Helper para normalizar textos
const normalize = (s) =>
  String(s ?? '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const MAX_IMPORT_FILE_SIZE_MB = 8;

const isPdfFile = (file) =>
  String(file?.type || '').includes('pdf') || String(file?.name || '').toLowerCase().endsWith('.pdf');

const isExcelFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
};

const parseBooleanValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;

  const normalized = normalize(value);
  if (!normalized) return null;

  if (['si', 'sí', 's', 'yes', 'true', '1', 'activo', 'activa', 'on'].includes(normalized)) return true;
  if (['no', 'n', 'false', '0', 'inactivo', 'inactiva', 'off'].includes(normalized)) return false;
  return null;
};

const parseNumberValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;

  const raw = String(value).trim();
  if (!raw) return null;

  let normalizedValue = raw.replace(/[$\s]/g, '');
  if (normalizedValue.includes(',') && normalizedValue.includes('.')) {
    normalizedValue = normalizedValue.replace(/\./g, '').replace(',', '.');
  } else if (normalizedValue.includes(',')) {
    normalizedValue = normalizedValue.replace(',', '.');
  }

  const num = Number.parseFloat(normalizedValue);
  return Number.isFinite(num) ? num : null;
};

const getFriendlyImportError = (error) => {
  const message = String(error?.message || error || '').trim();
  if (!message) return 'Ocurrio un error inesperado.';

  const normalizedMessage = normalize(message);

  if (normalizedMessage.includes('duplicate key')) return 'Ya existe un registro con esos datos.';
  if (normalizedMessage.includes('invalid input syntax')) return 'Hay un valor con formato invalido.';
  if (normalizedMessage.includes('violates foreign key constraint')) return 'Hay un dato relacionado que no existe en el sistema.';
  if (normalizedMessage.includes('null value')) return 'Falta completar un dato obligatorio.';
  if (normalizedMessage.includes('network')) return 'Se corto la conexion mientras se procesaba la importacion.';

  return message;
};

const buildInitialMap = (fileHeaders, fields) => {
  const initialMap = {};
  fields.forEach((field) => {
    const match = fileHeaders.find((header) => {
      const normalizedHeader = normalize(header);
      return (
        normalizedHeader === normalize(field.key) ||
        normalizedHeader === normalize(field.label) ||
        normalizedHeader.includes(normalize(field.label)) ||
        (field.aliases || []).some((alias) => normalizedHeader === normalize(alias) || normalizedHeader.includes(normalize(alias)))
      );
    });
    if (match) initialMap[field.key] = match;
  });
  return initialMap;
};

/* =========================================================================
   CONFIGURACIÓN DE TIPOS DE IMPORTACIÓN Y CAMPOS (Full Completo)
   ========================================================================= */
const IMPORT_TYPES = {
  PRODUCTOS: {
    id: 'PRODUCTOS',
    label: 'Productos (Inventario)',
    icon: <Package size={24} className="text-blue-500" />,
    description: 'Crear productos nuevos con stock inicial, costos, precios y configuración fiscal.',
    checkField: 'barcode', // Campo usado para validar duplicados
    fields: [
      { key: 'name', label: 'Nombre del Producto', required: true },
      { key: 'description', label: 'Descripcion', required: false },
      { key: 'barcode', label: 'Código de Barras (Único)', required: false },
      { key: 'category', label: 'Categoría', required: false },
      { key: 'brand', label: 'Marca', required: false }, // Nuevo
      { key: 'product_type', label: 'Tipo de Producto', required: false },
      { key: 'uom_name', label: 'Unidad de Medida', required: false },
      { key: 'cost_price', label: 'Costo (ARS)', required: false, type: 'number' },
      { key: 'target_margin', label: 'Margen Ganancia (%)', required: false, type: 'number' }, // Nuevo
      { key: 'iva_percentage', label: 'Alícuota IVA (%)', required: false, type: 'number' }, // Nuevo
      { key: 'sell_price', label: 'Precio Venta Final', required: false, type: 'number' },
      { key: 'price_usd', label: 'Precio USD (Opcional)', required: false, type: 'number' }, // Nuevo
      { key: 'stock', label: 'Stock Inicial', required: false, type: 'number' },
      { key: 'min_stock', label: 'Stock Mínimo (Alerta)', required: false, type: 'number' }, // Nuevo
      { key: 'active', label: 'Activo', required: false, type: 'boolean' },
      { key: 'auto_update_price', label: 'Auto Actualiza Precio', required: false, type: 'boolean' },
      { key: 'block_discounts', label: 'Bloquear Descuentos', required: false, type: 'boolean' },
      { key: 'block_surcharges', label: 'Bloquear Recargos', required: false, type: 'boolean' },
      { key: 'image_url', label: 'URL Imagen', required: false }, // Nuevo
    ]
  },
  CLIENTES: {
    id: 'CLIENTES',
    label: 'Clientes',
    icon: <Users size={24} className="text-emerald-500" />,
    description: 'Base de datos de clientes, límites de crédito y datos fiscales.',
    checkField: 'doc_number', // DNI o CUIT único
    fields: [
      { key: 'full_name', label: 'Razón Social / Nombre', required: true },
      { key: 'doc_number', label: 'DNI / CUIT (Sin guiones)', required: true },
      { key: 'tax_condition', label: 'Condición IVA (Resp. Inscripto, etc)', required: false }, // Nuevo
      { key: 'email', label: 'Email', required: false },
      { key: 'phone', label: 'WhatsApp / Teléfono', required: false },
      { key: 'address', label: 'Dirección (Calle y Altura)', required: false },
      { key: 'city', label: 'Ciudad / Localidad', required: false }, // Nuevo
      { key: 'state', label: 'Provincia', required: false }, // Nuevo
      { key: 'zip_code', label: 'Código Postal (CP)', required: false },
      { key: 'credit_limit', label: 'Límite de Crédito ($)', required: false, type: 'number' }, // Nuevo
      { key: 'balance', label: 'Saldo Inicial (Cta Cte)', required: false, type: 'number' },
    ]
  },
  PROVEEDORES: {
    id: 'PROVEEDORES',
    label: 'Proveedores',
    icon: <Truck size={24} className="text-amber-500" />,
    description: 'Proveedores, datos bancarios y condiciones comerciales.',
    checkField: 'cuit', // CUIT único
    fields: [
      { key: 'razon_social', label: 'Razón Social', required: true },
      { key: 'cuit', label: 'CUIT (Sin guiones)', required: true },
      { key: 'tax_condition', label: 'Condición IVA', required: false },
      { key: 'email', label: 'Email', required: false },
      { key: 'phone', label: 'Teléfono', required: false },
      { key: 'address', label: 'Dirección', required: false },
      { key: 'city', label: 'Ciudad', required: false },
      { key: 'state', label: 'Provincia', required: false },
      { key: 'cbu_alias', label: 'CBU / Alias', required: false }, // Nuevo
      { key: 'bank_name', label: 'Banco', required: false }, // Nuevo
      { key: 'saldo', label: 'Saldo Inicial ($)', required: false, type: 'number' },
    ]
  },
  // Mantenemos CC por compatibilidad, aunque el foco es altas
  CC_CLIENTES: {
    id: 'CC_CLIENTES',
    label: 'Historial CC Clientes',
    icon: <History size={24} className="text-emerald-600" />,
    description: 'Importar movimientos históricos de cuenta corriente.',
    checkField: null, // No chequea duplicados por ID, agrega movimientos
    fields: [
      { key: 'client_identifier', label: 'Cliente (DNI o Nombre Exacto)', required: true },
      { key: 'date', label: 'Fecha (YYYY-MM-DD)', required: true },
      { key: 'description', label: 'Concepto', required: true },
      { key: 'amount', label: 'Monto', required: true, type: 'number' },
    ]
  },
  CC_PROVEEDORES: {
    id: 'CC_PROVEEDORES',
    label: 'Historial CC Proveedores',
    icon: <History size={24} className="text-amber-600" />,
    description: 'Importar facturas o pagos históricos de proveedores.',
    checkField: null,
    fields: [
      { key: 'provider_identifier', label: 'Proveedor (CUIT o Razón Social)', required: true },
      { key: 'date', label: 'Fecha (YYYY-MM-DD)', required: true },
      { key: 'description', label: 'Concepto', required: true },
      { key: 'amount', label: 'Monto', required: true, type: 'number' },
    ]
  },
  ACTUALIZAR_STOCK: {
    id: 'ACTUALIZAR_STOCK',
    label: 'Actualizar Stock por Sucursal',
    icon: <Boxes size={24} className="text-violet-500" />,
    description: 'Actualizá el stock de productos en una sucursal específica usando nombre o código de barras.',
    checkField: null,
    requiresSite: true, // Este tipo requiere seleccionar una sucursal antes de subir el archivo
    fields: [
      { key: 'barcode', label: 'Código de Barras', required: false },
      { key: 'name', label: 'Nombre del Producto', required: false },
      { key: 'stock', label: 'Stock', required: true, type: 'number' },
    ]
  }
};

function Importaciones() {
  const navigate = useNavigate();
  const { profile, organization } = useAuth();
  const orgId = profile?.organization_id;

  // Estados del Wizard
  const [searchParams] = useSearchParams();
  const [activeType, setActiveType] = useState(null);
  const [step, setStep] = useState(1); // 1:Upload, 2:Map, 3:Preview, 4:Result

  // Datos
  const [fileName, setFileName] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawRows, setRawRows] = useState([]);
  const [colMapping, setColMapping] = useState({});

  // Logs de resultado
  const [importLogs, setImportLogs] = useState([]);
  const [expandedLogs, setExpandedLogs] = useState({});
  const [workingRow, setWorkingRow] = useState(null);
  const [workingAction, setWorkingAction] = useState('');

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState('');

  // Filtro de logs
  const [logFilter, setLogFilter] = useState('all'); // all, success, error, skipped

  // Sucursal (para ACTUALIZAR_STOCK)
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [availableSites, setAvailableSites] = useState([]);
  const currentConfig = activeType ? IMPORT_TYPES[activeType] : null;
  const stats = useMemo(() => (
    importLogs.reduce((acc, log) => {
      if (log.status === 'success') acc.success += 1;
      else if (log.status === 'error') acc.error += 1;
      else if (log.status === 'skipped') acc.skipped += 1;
      else if (log.status === 'pending') acc.pending += 1;
      return acc;
    }, { success: 0, error: 0, skipped: 0, pending: 0 })
  ), [importLogs]);

  const filteredLogs = useMemo(
    () => importLogs.filter((log) => logFilter === 'all' || log.status === logFilter),
    [importLogs, logFilter]
  );

  const hydrateImportedData = ({ importedRows, importedHeaders, sourceName, initialMap = {} }) => {
    setFileName(sourceName || '');
    setHeaders(importedHeaders || []);
    setRawRows(importedRows || []);
    setColMapping(initialMap);
    setImportLogs([]);
    setExpandedLogs({});
    setWorkingRow(null);
    setWorkingAction('');
    setLogFilter('all');
    setStep(2);
  };

  const getFieldDef = (typeId, fieldKey) =>
    IMPORT_TYPES[typeId]?.fields.find((field) => field.key === fieldKey) || null;

  const parseFieldValue = (value, fieldDef) => {
    if (!fieldDef) return String(value ?? '').trim();
    if (fieldDef.type === 'number') return parseNumberValue(value);
    if (fieldDef.type === 'boolean') return parseBooleanValue(value);
    return String(value ?? '').trim();
  };

  const buildDraftFromRawRow = (row, typeId = activeType) => {
    const typeConfig = IMPORT_TYPES[typeId];
    const draft = {};
    typeConfig?.fields.forEach((field) => {
      const header = colMapping[field.key];
      draft[field.key] = header ? row?.[header] ?? '' : '';
    });
    return draft;
  };

  const parseDraft = (draft, typeId = activeType) => {
    const typeConfig = IMPORT_TYPES[typeId];
    const parsed = {};
    typeConfig?.fields.forEach((field) => {
      parsed[field.key] = parseFieldValue(draft?.[field.key], field);
    });
    return parsed;
  };

  const resetImporter = () => {
    setActiveType(null);
    setStep(1);
    setFileName('');
    setHeaders([]);
    setRawRows([]);
    setColMapping({});
    setImportLogs([]);
    setExpandedLogs({});
    setWorkingRow(null);
    setWorkingAction('');
    setLogFilter('all');
    setSelectedSiteId(null);
    setAvailableSites([]);
  };

  const handleSelectType = async (typeId) => {
    setActiveType(typeId);
    const typeConfig = IMPORT_TYPES[typeId];
    if (typeConfig?.requiresSite && orgId) {
      const { data: sites } = await supabase
        .from('sitios')
        .select('id, nombre')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('nombre', { ascending: true });
      setAvailableSites(sites || []);
      if (sites?.length === 1) setSelectedSiteId(sites[0].id);
    }
  };

  // Deep-link: /sistema/importaciones?tipo=PRODUCTOS abre directo esa pestaña
  // (ej. desde el boton "Importar productos" del onboarding).
  useEffect(() => {
    const tipo = searchParams.get('tipo');
    if (tipo && IMPORT_TYPES[tipo] && activeType === null) {
      handleSelectType(tipo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const toggleExpandedLog = (row) => {
    setExpandedLogs((prev) => ({ ...prev, [row]: !prev[row] }));
  };

  const updateLogDraft = (row, fieldKey, value) => {
    setImportLogs((prev) => prev.map((log) => (
      log.row === row
        ? {
            ...log,
            draft: {
              ...(log.draft || {}),
              [fieldKey]: value,
            },
          }
        : log
    )));
  };

  const replaceLog = (row, nextLog) => {
    setImportLogs((prev) => prev.map((log) => (log.row === row ? { ...log, ...nextLog } : log)));
  };

  const setRowBusy = (row, action) => {
    setWorkingRow(row);
    setWorkingAction(action);
  };

  const clearRowBusy = () => {
    setWorkingRow(null);
    setWorkingAction('');
  };

  const loadImportContext = async (typeId) => {
    const context = {
      existingKeys: new Set(),
      productByBarcode: new Map(),
      productByName: new Map(),
      categoriesMap: {},
      unitsMap: {},
      usdExchangeRate: 0,
      defaultStockSiteId: null,
    };

    if (!orgId) return context;

    if (typeId === 'PRODUCTOS') {
      const products = await fetchAllPaginated(() =>
        supabase
          .from('products')
          .select('id, name, barcode')
          .eq('organization_id', orgId)
          .order('id', { ascending: true })
      );

      products?.forEach((product) => {
        if (product?.barcode) {
          context.productByBarcode.set(normalize(product.barcode), product);
        }
        if (product?.name) {
          context.productByName.set(normalize(product.name), product);
        }
      });

      const { data: cats } = await supabase
        .from('product_categories')
        .select('id, name')
        .eq('organization_id', orgId);
      cats?.forEach((category) => {
        if (category?.name) context.categoriesMap[normalize(category.name)] = category.id;
      });

      const { data: units } = await supabase
        .from('units_of_measure')
        .select('id, name, abbreviation')
        .eq('organization_id', orgId);
      units?.forEach((unit) => {
        if (unit?.name) context.unitsMap[normalize(unit.name)] = unit;
        if (unit?.abbreviation) context.unitsMap[normalize(unit.abbreviation)] = unit;
      });

      // organization viene del AuthContext (un fetch por sesión).
      const orgData = organization;
      context.usdExchangeRate = Number(orgData?.usd_exchange_rate || 0);
      context.defaultStockSiteId = orgData?.default_site_id || null;

      if (!context.defaultStockSiteId) {
        const { data: sites } = await supabase
          .from('sitios')
          .select('id, is_active')
          .eq('organization_id', orgId)
          .order('nombre', { ascending: true });

        context.defaultStockSiteId = resolvePreferredStockSiteId(orgData, sites || []);
      }
    } else if (typeId === 'CLIENTES') {
      const clients = await fetchAllPaginated(() =>
        supabase
          .from('clients')
          .select('doc_number')
          .eq('organization_id', orgId)
          .order('id', { ascending: true })
      );
      clients?.forEach((client) => {
        if (client?.doc_number) context.existingKeys.add(normalize(client.doc_number));
      });
    } else if (typeId === 'PROVEEDORES') {
      const providers = await fetchAllPaginated(() =>
        supabase
          .from('proveedores')
          .select('cuit')
          .eq('organization_id', orgId)
          .order('id', { ascending: true })
      );
      providers?.forEach((provider) => {
        if (provider?.cuit) context.existingKeys.add(normalize(provider.cuit));
      });
    } else if (typeId === 'ACTUALIZAR_STOCK') {
      // Cargar todos los productos para matchear por barcode o nombre
      const products = await fetchAllPaginated(() =>
        supabase
          .from('products')
          .select('id, name, barcode')
          .eq('organization_id', orgId)
          .order('id', { ascending: true })
      );

      products?.forEach((product) => {
        if (product?.barcode) {
          context.productByBarcode.set(normalize(product.barcode), product);
        }
        if (product?.name) {
          context.productByName.set(normalize(product.name), product);
        }
      });

      // La sucursal ya fue seleccionada por el usuario (selectedSiteId)
      context.selectedSiteId = selectedSiteId;
    }

    return context;
  };

  const ensureProductCategory = async (categoryName, context) => {
    if (!categoryName) return null;

    const normalizedCategory = normalize(categoryName);
    if (context.categoriesMap[normalizedCategory]) {
      return context.categoriesMap[normalizedCategory];
    }

    const { data: newCategory, error } = await supabase
      .from('product_categories')
      .insert({
        organization_id: orgId,
        name: categoryName,
        is_active: true,
      })
      .select('id')
      .single();

    if (error) {
      console.error('Error creando categoria:', error);
      return null;
    }

    context.categoriesMap[normalizedCategory] = newCategory.id;
    return newCategory.id;
  };

  const ensureUnitOfMeasure = async (uomName, context) => {
    if (!uomName) return null;

    const normalizedUom = normalize(uomName);
    if (context.unitsMap[normalizedUom]?.id) {
      return context.unitsMap[normalizedUom].id;
    }

    const unitName = String(uomName).trim();
    const { data: newUnit, error } = await supabase
      .from('units_of_measure')
      .insert({
        organization_id: orgId,
        name: unitName.toUpperCase(),
        abbreviation: unitName.slice(0, 10).toLowerCase(),
      })
      .select('id, name, abbreviation')
      .single();

    if (error) {
      console.error('Error creando unidad:', error);
      return null;
    }

    context.unitsMap[normalizedUom] = newUnit;
    if (newUnit?.name) context.unitsMap[normalize(newUnit.name)] = newUnit;
    if (newUnit?.abbreviation) context.unitsMap[normalize(newUnit.abbreviation)] = newUnit;
    return newUnit.id;
  };

  const updateExistingProductStock = async ({
    productId,
    stock,
    minStock,
    defaultStockSiteId,
  }) => {
    if (stock === null || stock === undefined) {
      return { updated: false, stockValue: null };
    }

    const stockValue = Number(stock) || 0;

    await supabase
      .from('product_variants')
      .update({ stock_quantity: stockValue })
      .eq('product_id', productId);

    if (defaultStockSiteId) {
      const { error: stockLevelErr } = await supabase
        .from('stock_levels')
        .upsert({
          organization_id: orgId,
          product_id: productId,
          site_id: defaultStockSiteId,
          quantity: stockValue,
          min_stock: Number(minStock) || 0,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'product_id,site_id' });

      if (stockLevelErr) throw stockLevelErr;
    }

    return { updated: true, stockValue };
  };

  const processProductRow = async ({ draft, rowIndex, context }) => {
    const parsed = parseDraft(draft, 'PRODUCTOS');
    const barcode = parsed.barcode;
    const name = parsed.name;
    const description = parsed.description;
    const categoryName = parsed.category || 'General';
    const uomName = parsed.uom_name;
    const productType = parsed.product_type || 'Producto Terminado';
    const costPrice = Number(parsed.cost_price || 0);
    const targetMargin = Number(parsed.target_margin || 0);
    let sellPrice = Number(parsed.sell_price || 0);
    let priceUsd = Number(parsed.price_usd || 0);
    const stockInitial = parsed.stock;
    const minStock = Number(parsed.min_stock || 0);
    const ivaPercentage = Number(parsed.iva_percentage || 21);
    const activeFlag = parsed.active;
    const autoUpdatePrice = parsed.auto_update_price;
    const blockDiscounts = parsed.block_discounts;
    const blockSurcharges = parsed.block_surcharges;
    const brand = parsed.brand;
    const imageUrl = parsed.image_url;

    if (!name) throw new Error('Falta el nombre del producto.');

    if (!sellPrice && costPrice > 0 && targetMargin > 0) {
      sellPrice = Number((costPrice * (1 + targetMargin / 100)).toFixed(2));
    }
    if (!sellPrice && priceUsd > 0 && context.usdExchangeRate > 0) {
      sellPrice = Number((priceUsd * context.usdExchangeRate).toFixed(2));
    }
    if (!priceUsd && sellPrice > 0 && context.usdExchangeRate > 0) {
      priceUsd = Number((sellPrice / context.usdExchangeRate).toFixed(2));
    }

    const normalizedBarcode = barcode ? normalize(barcode) : '';
    const normalizedName = normalize(name);
    const existingProduct = normalizedBarcode
      ? context.productByBarcode.get(normalizedBarcode)
      : context.productByName.get(normalizedName);

    if (existingProduct) {
      const stockResult = await updateExistingProductStock({
        productId: existingProduct.id,
        stock: stockInitial,
        minStock,
        defaultStockSiteId: context.defaultStockSiteId,
      });

      if (normalizedBarcode && normalize(existingProduct.name) !== normalizedName) {
        return {
          row: rowIndex,
          status: 'pending',
          msg: stockResult.updated
            ? `Se encontro el producto por codigo de barras y se actualizo el stock, pero el nombre importado no coincide.`
            : `Se encontro el producto por codigo de barras, pero el nombre importado no coincide.`,
          draft,
          context: {
            kind: 'product_name_conflict',
            productId: existingProduct.id,
            barcode,
            currentName: existingProduct.name || '',
            importedName: name,
            stockUpdated: stockResult.updated,
            stockValue: stockResult.stockValue,
          },
        };
      }

      if (stockResult.updated) {
        return {
          row: rowIndex,
          status: 'success',
          msg: normalizedBarcode
            ? `Producto existente por codigo de barras. Stock actualizado a ${stockResult.stockValue}.`
            : `Producto existente por nombre. Stock actualizado a ${stockResult.stockValue}.`,
          draft,
        };
      }

      return {
        row: rowIndex,
        status: 'skipped',
        msg: normalizedBarcode
          ? `Producto existente por codigo de barras. No se aplicaron cambios.`
          : `Producto existente por nombre. No se aplicaron cambios.`,
        draft,
      };
    }

    const finalCategoryId = await ensureProductCategory(categoryName, context);
    const finalUomId = await ensureUnitOfMeasure(uomName, context);

    const { data: newProduct, error: productError } = await supabase
      .from('products')
      .insert({
        organization_id: orgId,
        name: String(name).trim().toUpperCase(),
        description: description || null,
        barcode: barcode || null,
        category: categoryName,
        category_id: finalCategoryId,
        brand: brand || null,
        product_type: productType,
        uom_id: finalUomId,
        cost_price: costPrice,
        sell_price: sellPrice,
        iva_percentage: ivaPercentage,
        price_usd: priceUsd,
        target_margin: targetMargin,
        min_stock: minStock,
        image_url: imageUrl || null,
        active: activeFlag ?? true,
        is_active: activeFlag ?? true,
        auto_update_price: autoUpdatePrice ?? false,
        block_discounts: blockDiscounts ?? false,
        block_surcharges: blockSurcharges ?? false,
      })
      .select()
      .single();

    if (productError) throw productError;

    if (newProduct) {
      await supabase.from('product_variants').insert({
        organization_id: orgId,
        product_id: newProduct.id,
        stock_quantity: Number(stockInitial || 0),
        cost_price: costPrice,
        price: sellPrice,
        barcode: barcode || null,
        attributes: { variant_name: 'Base', variant_description: '', variant_active: true },
      });

      if (context.defaultStockSiteId) {
        const { error: stockErr } = await supabase.from('stock_levels').upsert({
          organization_id: orgId,
          product_id: newProduct.id,
          site_id: context.defaultStockSiteId,
          quantity: Number(stockInitial || 0),
          min_stock: minStock,
        }, { onConflict: 'product_id,site_id' });

        if (stockErr) throw stockErr;
      }

      if (barcode) {
        context.productByBarcode.set(normalize(barcode), newProduct);
      }
      context.productByName.set(normalize(newProduct.name || name), {
        id: newProduct.id,
        name: newProduct.name || String(name).trim().toUpperCase(),
        barcode: newProduct.barcode || null,
      });
    }

    return {
      row: rowIndex,
      status: 'success',
      msg: context.defaultStockSiteId
        ? `${name} creado correctamente.`
        : `${name} creado, pero falta una sucursal predeterminada para reflejar el stock en inventario.`,
      draft,
    };
  };

  const processClientRow = async ({ draft, rowIndex, context }) => {
    const parsed = parseDraft(draft, 'CLIENTES');
    const name = parsed.full_name;
    const doc = parsed.doc_number;

    if (!name) throw new Error('Falta el nombre o razon social del cliente.');
    if (!doc) throw new Error('Falta el DNI o CUIT del cliente.');

    if (context.existingKeys.has(normalize(doc))) {
      return {
        row: rowIndex,
        status: 'skipped',
        msg: `Cliente existente. Ya hay un registro con DNI/CUIT ${doc}.`,
        draft,
      };
    }

    const { error } = await supabase.from('clients').insert({
      organization_id: orgId,
      full_name: name,
      doc_number: doc,
      email: parsed.email || null,
      phone: parsed.phone || null,
      address: parsed.address || null,
      city: parsed.city || null,
      state: parsed.state || null,
      zip_code: parsed.zip_code || null,
      tax_condition: parsed.tax_condition || 'Consumidor Final',
      credit_limit: parsed.credit_limit,
      balance: parsed.balance,
      is_active: true,
    });

    if (error) throw error;

    context.existingKeys.add(normalize(doc));
    return {
      row: rowIndex,
      status: 'success',
      msg: `${name} creado correctamente.`,
      draft,
    };
  };

  const processProviderRow = async ({ draft, rowIndex, context }) => {
    const parsed = parseDraft(draft, 'PROVEEDORES');
    const razonSocial = parsed.razon_social;
    const cuit = parsed.cuit;

    if (!razonSocial) throw new Error('Falta la razon social del proveedor.');
    if (!cuit) throw new Error('Falta el CUIT del proveedor.');

    if (context.existingKeys.has(normalize(cuit))) {
      return {
        row: rowIndex,
        status: 'skipped',
        msg: `Proveedor existente. Ya hay un registro con CUIT ${cuit}.`,
        draft,
      };
    }

    const { error } = await supabase.from('proveedores').insert({
      organization_id: orgId,
      razon_social: razonSocial,
      cuit,
      email: parsed.email || null,
      telefono: parsed.phone || null,
      direccion: parsed.address || null,
      tax_condition: parsed.tax_condition || 'Responsable Inscripto',
      cbu_alias: parsed.cbu_alias || null,
      bank_name: parsed.bank_name || null,
      saldo: parsed.saldo,
      is_active: true,
    });

    if (error) throw error;

    context.existingKeys.add(normalize(cuit));
    return {
      row: rowIndex,
      status: 'success',
      msg: `${razonSocial} creado correctamente.`,
      draft,
    };
  };

  const processCcRow = async ({ draft, rowIndex }) => ({
    row: rowIndex,
    status: 'skipped',
    msg: 'La importacion de cuenta corriente sigue deshabilitada en esta vista.',
    draft,
  });

  const processStockRow = async ({ draft, rowIndex, context }) => {
    const parsed = parseDraft(draft, 'ACTUALIZAR_STOCK');
    const barcode = String(parsed.barcode || '').trim();
    const name = String(parsed.name || '').trim();
    const stockValue = parsed.stock;

    if (stockValue === null || stockValue === undefined || stockValue === '') {
      return { row: rowIndex, status: 'error', msg: 'Falta el valor de stock.', draft };
    }

    if (!barcode && !name) {
      return { row: rowIndex, status: 'error', msg: 'Falta código de barras o nombre del producto.', draft };
    }

    if (!context.selectedSiteId) {
      return { row: rowIndex, status: 'error', msg: 'No se seleccionó ninguna sucursal.', draft };
    }

    // Buscar producto: primero por código de barras, luego por nombre
    let product = null;
    let matchedBy = '';

    if (barcode) {
      product = context.productByBarcode.get(normalize(barcode)) || null;
      if (product) matchedBy = 'código de barras';
    }

    if (!product && name) {
      product = context.productByName.get(normalize(name)) || null;
      if (product) matchedBy = 'nombre';
    }

    if (!product) {
      const searchTerm = barcode || name;
      return {
        row: rowIndex,
        status: 'error',
        msg: `Producto no encontrado: "${searchTerm}".`,
        draft,
      };
    }

    const qty = Number(stockValue) || 0;

    // Actualizar product_variants
    await supabase
      .from('product_variants')
      .update({ stock_quantity: qty })
      .eq('product_id', product.id);

    // Upsert en stock_levels para la sucursal seleccionada
    const { error: stockErr } = await supabase
      .from('stock_levels')
      .upsert({
        organization_id: orgId,
        product_id: product.id,
        site_id: context.selectedSiteId,
        quantity: qty,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'product_id,site_id' });

    if (stockErr) {
      return {
        row: rowIndex,
        status: 'error',
        msg: `Error al actualizar stock de "${product.name}": ${stockErr.message}`,
        draft,
      };
    }

    return {
      row: rowIndex,
      status: 'success',
      msg: `Stock de "${product.name}" actualizado a ${qty} (por ${matchedBy}).`,
      draft,
    };
  };

  const processImportRow = async ({ draft, rowIndex, typeId, context }) => {
    if (typeId === 'PRODUCTOS') return processProductRow({ draft, rowIndex, context });
    if (typeId === 'CLIENTES') return processClientRow({ draft, rowIndex, context });
    if (typeId === 'PROVEEDORES') return processProviderRow({ draft, rowIndex, context });
    if (typeId === 'ACTUALIZAR_STOCK') return processStockRow({ draft, rowIndex, context });
    return processCcRow({ draft, rowIndex, context });
  };

  /* =========================================================================
     PASO 1: LEER EXCEL
     ========================================================================= */
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !currentConfig) return;

    if (isPdfFile(file)) {
      setFileName(file.name);
      setLoading(true);

      try {
        if (file.size > MAX_IMPORT_FILE_SIZE_MB * 1024 * 1024) {
          throw new Error(`El PDF es muy pesado. Usa uno menor a ${MAX_IMPORT_FILE_SIZE_MB}MB.`);
        }

        const fileData = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => reject(new Error('No se pudo leer el PDF.'));
          reader.readAsDataURL(file);
        });

        const { data: sessionData } = await supabase.auth.getSession();
        const accessToken = sessionData?.session?.access_token;
        if (!accessToken) {
          throw new Error('No hay sesion activa. Recarga la pagina.');
        }

        const { data, error } = await supabase.functions.invoke('ocr-importaciones', {
          body: {
            fileData,
            fileName: file.name,
            fileType: file.type || 'application/pdf',
            importType: currentConfig.id,
            importLabel: currentConfig.label,
            fields: currentConfig.fields.map((field) => ({
              key: field.key,
              label: field.label,
              type: field.type || 'string',
              required: Boolean(field.required)
            }))
          },
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        });

        if (error) {
          throw new Error(error.message || 'Error al conectar con el lector PDF.');
        }

        if (data?.ok === false) {
          throw new Error(data.error || 'No se pudo leer el PDF.');
        }

        const structuredRows = Array.isArray(data?.data?.rows) ? data.data.rows : [];
        const importedHeaders = currentConfig.fields.map((field) => field.label);
        const importedRows = structuredRows
          .map((row) => {
            const nextRow = {};
            currentConfig.fields.forEach((field) => {
              nextRow[field.label] = row?.[field.key] ?? '';
            });
            return nextRow;
          })
          .filter((row) => Object.values(row).some((value) => String(value ?? '').trim() !== ''));

        if (!importedRows.length) {
          throw new Error('El PDF no devolvio filas utilizables para importar.');
        }

        const initialMap = {};
        currentConfig.fields.forEach((field) => {
          initialMap[field.key] = field.label;
        });

        hydrateImportedData({
          importedRows,
          importedHeaders,
          sourceName: file.name,
          initialMap
        });

        toast.success('PDF procesado. Revisa el mapeo antes de importar.');
        return;
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Error leyendo el PDF');
        return;
      } finally {
        setLoading(false);
        if (e.target) e.target.value = '';
      }
    }

    if (!isExcelFile(file)) {
      toast.error('Formato no soportado. Usa Excel o PDF.');
      if (e.target) e.target.value = '';
      return;
    }

    let XLSX;
    try {
      const mod = await import('xlsx');
      XLSX = mod?.default || mod;
    } catch (err) {
      toast.error('Error cargando librería XLSX');
      return;
    }

    setFileName(file.name);
    setLoading(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (!jsonData || jsonData.length === 0) {
        toast.error('El archivo está vacío');
        return;
      }

      const fileHeaders = Object.keys(jsonData[0]);
      hydrateImportedData({
        importedRows: jsonData,
        importedHeaders: fileHeaders,
        sourceName: file.name,
        initialMap: buildInitialMap(fileHeaders, currentConfig.fields)
      });
    } catch (error) {
      console.error(error);
      toast.error('Error leyendo el archivo Excel');
    } finally {
      setLoading(false);
      if (e.target) e.target.value = '';
    }
  };

  /* =========================================================================
     PASO 3: EJECUTAR IMPORTACIÓN CON VALIDACIÓN
     ========================================================================= */
  const executeImport = async () => {
    if (!orgId) return;
    setLoading(true);
    setProgress('Analizando datos existentes...');

    try {
      const logs = [];
      const context = await loadImportContext(activeType);

      for (let i = 0; i < rawRows.length; i++) {
        const row = rawRows[i];
        const rowIndex = i + 1;
        setProgress(`Procesando fila ${rowIndex}/${rawRows.length}...`);

        try {
          const draft = buildDraftFromRawRow(row, activeType);
          const result = await processImportRow({
            draft,
            rowIndex,
            typeId: activeType,
            context,
          });
          logs.push(result);
        } catch (err) {
          console.error(err);
          logs.push({
            row: rowIndex,
            status: 'error',
            msg: getFriendlyImportError(err),
            draft: buildDraftFromRawRow(row, activeType),
          });
        }
      }

      setImportLogs(logs);
      setExpandedLogs({});
      setStep(4);
      toast.success('Proceso finalizado. Revisa los resultados.');

    } catch (criticalError) {
      console.error(criticalError);
      toast.error(getFriendlyImportError(criticalError));
    } finally {
      setLoading(false);
      setProgress('');
    }
  };

  const retryImportLog = async (log) => {
    if (!log?.draft) return;

    setRowBusy(log.row, 'retry');
    try {
      const context = await loadImportContext(activeType);
      const nextLog = await processImportRow({
        draft: log.draft,
        rowIndex: log.row,
        typeId: activeType,
        context,
      });

      replaceLog(log.row, nextLog);
      setExpandedLogs((prev) => ({ ...prev, [log.row]: nextLog.status === 'error' || nextLog.status === 'pending' }));
      toast.success('La fila se reproceso correctamente.');
    } catch (error) {
      console.error(error);
      replaceLog(log.row, {
        status: 'error',
        msg: getFriendlyImportError(error),
      });
      toast.error(getFriendlyImportError(error));
    } finally {
      clearRowBusy();
    }
  };

  const resolveNameConflict = async (log, shouldApplyImportedName) => {
    if (!log?.context?.productId) return;

    setRowBusy(log.row, shouldApplyImportedName ? 'apply-name' : 'keep-name');
    try {
      if (shouldApplyImportedName) {
        const { error } = await supabase
          .from('products')
          .update({ name: String(log.context.importedName || '').trim().toUpperCase() })
          .eq('id', log.context.productId);

        if (error) throw error;
      }

      replaceLog(log.row, {
        status: 'success',
        msg: shouldApplyImportedName
          ? `Se actualizo el nombre del producto a "${log.context.importedName}".`
          : `Se mantuvo el nombre actual "${log.context.currentName}".`,
      });
      setExpandedLogs((prev) => ({ ...prev, [log.row]: false }));
      toast.success('La revision del nombre se guardo correctamente.');
    } catch (error) {
      console.error(error);
      replaceLog(log.row, {
        status: 'error',
        msg: getFriendlyImportError(error),
      });
      toast.error(getFriendlyImportError(error));
    } finally {
      clearRowBusy();
    }
  };


  /* =========================================================================
     RENDER
     ========================================================================= */
  
  // 0. MENU PRINCIPAL
  if (!activeType) {
    return (
      <div className="p-8 bg-[#F2F9FF] min-h-screen animate-fade-in">
        <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black text-slate-900 uppercase tracking-tight">Centro de Importación</h1>
              <p className="text-sm text-slate-500 font-medium mt-1">Carga masiva de datos desde Excel o PDF para poblar tu sistema {BRAND_NAME}.</p>
            </div>
            <button onClick={() => navigate('/sistema/configuracion')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors px-4 py-2 bg-white rounded-xl border border-slate-200">
                <ArrowLeft size={18} /> <span className="text-[11px] font-black uppercase tracking-widest">Salir</span>
            </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Object.values(IMPORT_TYPES).map(type => (
                <button
                   key={type.id}
                   data-onboarding-id={`import-type-${type.id}`}
                   onClick={() => handleSelectType(type.id)}
                   className="group bg-white p-6 rounded-3xl border border-slate-200 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all text-left flex flex-col h-48 justify-between relative overflow-hidden"
                >
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                       {React.cloneElement(type.icon, { size: 80 })}
                    </div>
                    
                    <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center group-hover:bg-blue-50 group-hover:scale-110 transition-all">
                        {type.icon}
                    </div>
                    
                    <div className="relative z-10">
                        <h3 className="text-base font-black text-slate-800 uppercase tracking-wide">{type.label}</h3>
                        <p className="text-xs text-slate-400 mt-2 font-medium leading-relaxed">{type.description}</p>
                    </div>
                </button>
            ))}
        </div>
      </div>
    );
  }
  return (
    <div className="p-6 bg-[#F2F9FF] min-h-screen flex flex-col">
        {/* Header con Pasos */}
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-4 mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4 w-full md:w-auto">
                <button onClick={resetImporter} 
                        className="w-10 h-10 rounded-xl bg-slate-50 hover:bg-slate-100 flex items-center justify-center text-slate-500 transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400">Importador Masivo</div>
                    <div className="text-lg font-black text-slate-900 uppercase flex items-center gap-2">
                        {currentConfig.label}
                    </div>
                    {currentConfig?.requiresSite && selectedSiteId && availableSites.length > 0 && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                            <Building2 size={11} className="text-violet-400" />
                            <span className="text-[10px] font-bold text-violet-500 uppercase tracking-wide">
                                {availableSites.find(s => s.id === selectedSiteId)?.nombre || ''}
                            </span>
                        </div>
                    )}
                </div>
            </div>

            {/* Steps Visual */}
            <div className="flex items-center gap-2 bg-slate-50 px-4 py-2 rounded-2xl">
                {[
                  { n: 1, l: 'Cargar' },
                  { n: 2, l: 'Mapear' },
                  { n: 3, l: 'Confirmar' },
                  { n: 4, l: 'Resultados' }
                ].map((s, idx) => (
                   <React.Fragment key={s.n}>
                      <div className={`flex items-center gap-2 ${step === s.n ? 'text-blue-600' : step > s.n ? 'text-emerald-500' : 'text-slate-300'}`}>
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black border 
                              ${step === s.n ? 'bg-blue-100 border-blue-200' : step > s.n ? 'bg-emerald-100 border-emerald-200' : 'bg-white border-slate-200'}`}>
                              {step > s.n ? <CheckCircle2 size={14}/> : s.n}
                          </div>
                          <span className="text-[10px] font-black uppercase hidden md:block">{s.l}</span>
                      </div>
                      {idx < 3 && <div className="w-4 h-[1px] bg-slate-200" />}
                   </React.Fragment>
                ))}
            </div>
        </div>

        {/* CONTENIDO PRINCIPAL */}
        <div className="flex-1 bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col relative">
            
            {/* STEP 1: UPLOAD */}
            {step === 1 && (
                <div className="flex-1 flex flex-col items-center justify-center p-10 text-center animate-fade-in">
                    <div className="w-24 h-24 bg-blue-50/50 rounded-full flex items-center justify-center mb-6 ring-8 ring-blue-50">
                        <UploadCloud size={40} className="text-blue-500" />
                    </div>
                    <h3 className="text-xl font-black text-slate-800 uppercase mb-3">Subí tu archivo</h3>
                    <p className="text-sm text-slate-500 max-w-md mb-8 leading-relaxed">
                        El archivo puede ser <strong>.xlsx</strong> o <strong>.xls</strong>.
                        La primera fila debe contener los títulos de columna.
                    </p>

                    {/* Selector de sucursal (solo para ACTUALIZAR_STOCK) */}
                    {currentConfig?.requiresSite && (
                        <div className="mb-8 w-full max-w-sm">
                            <div className="flex items-center gap-2 justify-center mb-3">
                                <Building2 size={16} className="text-violet-500" />
                                <span className="text-xs font-black uppercase tracking-widest text-slate-600">Sucursal a actualizar</span>
                            </div>
                            {availableSites.length === 0 ? (
                                <div className="text-xs text-slate-400 bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">
                                    No hay sucursales activas disponibles.
                                </div>
                            ) : (
                                <select
                                    className="w-full text-sm font-bold rounded-2xl px-4 py-3 border border-violet-200 bg-violet-50 text-violet-900 outline-none focus:ring-2 focus:ring-violet-300 transition-all"
                                    value={selectedSiteId || ''}
                                    onChange={(e) => setSelectedSiteId(e.target.value || null)}
                                >
                                    <option value="">— Seleccioná una sucursal —</option>
                                    {availableSites.map(site => (
                                        <option key={site.id} value={site.id}>{site.nombre}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    )}

                    <label data-onboarding-id="import-excel-file-label" className={`cursor-pointer group relative ${currentConfig?.requiresSite && !selectedSiteId ? 'opacity-40 pointer-events-none' : ''}`}>
                        <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFileUpload} disabled={loading || (currentConfig?.requiresSite && !selectedSiteId)} />
                        <div className="px-10 py-5 bg-[#0B2A36] text-white rounded-2xl font-black uppercase tracking-widest hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 group-hover:-translate-y-1 flex items-center gap-3">
                            {loading ? <div className="animate-spin w-5 h-5 border-2 border-white/30 border-t-white rounded-full"></div> : <FileSpreadsheet size={20} />}
                            <span>{loading ? 'Procesando...' : 'Seleccionar Excel'}</span>
                        </div>
                    </label>
                    {currentConfig?.requiresSite && !selectedSiteId && (
                        <p className="mt-3 text-xs text-violet-500 font-semibold">Primero seleccioná una sucursal para continuar</p>
                    )}

                    {/* Ayuda campos */}
                    <div className="mt-12 max-w-2xl w-full">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-4 text-center">Datos que podés importar</div>
                        <div className="flex flex-wrap justify-center gap-2">
                            {currentConfig.fields.map(f => (
                                <div key={f.key} className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase flex items-center gap-1.5
                                    ${f.required ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                                    {f.required && <AlertCircle size={10} />}
                                    {f.label}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* STEP 2: MAPEO */}
            {step === 2 && (
                <div data-onboarding-id="import-excel-mapping-container" className="flex-1 p-6 flex flex-col animate-fade-in">
                    <div className="mb-6 flex items-center justify-between">
                        <div>
                            <h3 className="text-lg font-black text-slate-800 uppercase">Validar Columnas</h3>
                            <p className="text-xs text-slate-500">Asociá las columnas detectadas con los campos de {BRAND_NAME}.</p>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto pb-20">
                        {currentConfig.fields.map(field => {
                            const isMapped = !!colMapping[field.key];
                            return (
                                <div key={field.key} className={`p-4 rounded-2xl border transition-all ${isMapped ? 'bg-white border-slate-200' : 'bg-rose-50 border-rose-100'}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2">
                                            <label className="text-[11px] font-black uppercase text-slate-700">{field.label}</label>
                                            {field.required && <span className="text-[9px] font-black text-white bg-rose-500 px-1.5 py-0.5 rounded">OBLIGATORIO</span>}
                                        </div>
                                    </div>
                                    
                                    <select
                                        className={`w-full text-xs font-bold rounded-xl px-3 py-2.5 outline-none border focus:ring-2 focus:ring-blue-100 transition-all ${isMapped ? 'border-slate-300 text-slate-700' : 'border-rose-300 text-rose-700 bg-white'}`}
                                        value={colMapping[field.key] || ''}
                                        onChange={(e) => setColMapping(prev => ({...prev, [field.key]: e.target.value}))}
                                    >
                                        <option value="">(Sin asignar)</option>
                                        {headers.map(h => (
                                            <option key={h} value={h}>{h}</option>
                                        ))}
                                    </select>
                                    
                                    <div className="mt-2 text-[10px] text-slate-400 font-medium truncate flex items-center gap-1 h-4">
                                        <span className="uppercase tracking-widest text-[9px]">Ej:</span> 
                                        <span className="text-slate-600">{rawRows[0] ? rawRows[0][colMapping[field.key]] : '-'}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer Actions */}
                    <div className="absolute bottom-0 left-0 w-full bg-white border-t border-slate-100 p-4 flex justify-between items-center z-10">
                         <button onClick={() => setStep(1)} className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs uppercase hover:bg-slate-50 transition-colors">
                            Volver
                         </button>
                         <button 
                            onClick={() => {
                                const missing = currentConfig.fields.filter(f => f.required && !colMapping[f.key]);
                                if (missing.length > 0) {
                                    toast.error(`Faltan campos obligatorios: ${missing.map(m=>m.label).join(', ')}`);
                                    return;
                                }
                                setStep(3);
                            }} 
                            className="px-8 py-3 rounded-xl bg-blue-600 text-white font-black text-xs uppercase hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center gap-2"
                         >
                            Ver Previsualización <ChevronRight size={16} />
                         </button>
                    </div>
                </div>
            )}

            {/* STEP 3: PREVIEW */}
            {step === 3 && (
                <div className="flex-1 flex flex-col h-full animate-fade-in">
                    <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
                        <div>
                            <h3 className="text-sm font-black text-slate-800 uppercase flex items-center gap-2">
                                <FileSpreadsheet size={16} className="text-blue-500" />
                                {rawRows.length} Registros detectados
                            </h3>
                            <p className="text-[11px] text-slate-500 mt-1 font-medium">
                                Revisa los datos antes de importar. Si una fila necesita revision, la vas a poder resolver despues desde resultados.
                            </p>
                        </div>
                        
                        <button
                           data-onboarding-id="import-excel-preview-submit"
                           onClick={executeImport}
                           disabled={loading}
                           className="px-6 py-3 bg-[#3FF2AA] text-[#0B2A36] rounded-xl font-black text-xs uppercase tracking-widest hover:shadow-lg transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? <div className="animate-spin w-4 h-4 border-2 border-current border-t-transparent rounded-full"/> : <Save size={16} />}
                            {loading ? progress : 'Confirmar Importación'}
                        </button>
                    </div>

                    <div className="flex-1 overflow-auto bg-slate-50">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-white text-[10px] font-black text-slate-500 uppercase tracking-wider sticky top-0 z-10 shadow-sm">
                                    <th className="px-4 py-3 border-b border-slate-100 w-12 text-center">#</th>
                                    {currentConfig.fields.map(f => (
                                        colMapping[f.key] && (
                                            <th key={f.key} className="px-4 py-3 border-b border-slate-100 whitespace-nowrap min-w-[120px]">
                                                {f.label}
                                            </th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 bg-white">
                                {rawRows.slice(0, 50).map((row, i) => (
                                    <tr key={i} className="hover:bg-blue-50/30 transition-colors text-xs text-slate-600 font-medium">
                                        <td className="px-4 py-3 text-slate-400 font-mono text-[10px] text-center">{i + 1}</td>
                                        {currentConfig.fields.map(f => (
                                            colMapping[f.key] && (
                                                <td key={f.key} className="px-4 py-3 whitespace-nowrap">
                                                    {String(row[colMapping[f.key]] ?? '')}
                                                </td>
                                            )
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {rawRows.length > 50 && (
                            <div className="p-4 text-center text-xs text-slate-400 font-bold uppercase tracking-widest">
                                ... y {rawRows.length - 50} filas más
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* STEP 4: RESULTADOS (LOGS) */}
            {step === 4 && (
                <div className="flex-1 p-6 flex flex-col animate-fade-in">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 gap-4">
                         <h3 className="text-lg font-black text-slate-800 uppercase">Resultado de importacion</h3>
                         <div className="flex flex-wrap gap-2">
                             <button onClick={() => setLogFilter('all')} className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${logFilter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>Todos: {importLogs.length}</button>
                             <button onClick={() => setLogFilter('success')} className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${logFilter === 'success' ? 'bg-emerald-600 text-white' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'}`}>Exitosos: {stats.success}</button>
                             <button onClick={() => setLogFilter('pending')} className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${logFilter === 'pending' ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700 hover:bg-blue-200'}`}>Revisar: {stats.pending}</button>
                             <button onClick={() => setLogFilter('skipped')} className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${logFilter === 'skipped' ? 'bg-amber-600 text-white' : 'bg-amber-100 text-amber-700 hover:bg-amber-200'}`}>Omitidos: {stats.skipped}</button>
                             <button onClick={() => setLogFilter('error')} className={`px-3 py-1 rounded-lg text-[10px] font-black uppercase transition-all ${logFilter === 'error' ? 'bg-rose-600 text-white' : 'bg-rose-100 text-rose-700 hover:bg-rose-200'}`}>Errores: {stats.error}</button>
                         </div>
                    </div>

                    <div className="flex-1 bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col">
                        <div className="overflow-y-auto flex-1 p-0">
                            <table className="w-full">
                                <thead className="sticky top-0 bg-slate-50 z-10">
                                    <tr className="text-[10px] font-black uppercase text-slate-500 tracking-wider">
                                        <th className="px-4 py-3 text-left w-16">Fila</th>
                                        <th className="px-4 py-3 text-left w-32">Estado</th>
                                        <th className="px-4 py-3 text-left">Detalle</th>
                                        <th className="px-4 py-3 text-right w-40">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100">
                                    {filteredLogs.map((log) => {
                                        const isExpanded = Boolean(expandedLogs[log.row]);
                                        const isBusy = workingRow === log.row;
                                        const canReview = log.status === 'error' || log.status === 'pending';

                                        return (
                                            <React.Fragment key={log.row}>
                                                <tr className="text-xs">
                                                    <td className="px-4 py-3 font-mono text-slate-400">#{log.row}</td>
                                                    <td className="px-4 py-3">
                                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide
                                                            ${log.status === 'success' ? 'bg-emerald-50 text-emerald-600' :
                                                              log.status === 'skipped' ? 'bg-amber-50 text-amber-600' :
                                                              log.status === 'pending' ? 'bg-blue-50 text-blue-600' : 'bg-rose-50 text-rose-600'}`}>
                                                            {log.status === 'success' && <CheckCircle2 size={12} />}
                                                            {log.status === 'skipped' && <AlertTriangle size={12} />}
                                                            {log.status === 'pending' && <AlertCircle size={12} />}
                                                            {log.status === 'error' && <XCircle size={12} />}
                                                            {log.status === 'success' ? 'Listo' : log.status === 'skipped' ? 'Omitido' : log.status === 'pending' ? 'Revisar' : 'Error'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-3 text-slate-600 font-medium">
                                                        {log.msg}
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {canReview ? (
                                                            <button
                                                                onClick={() => toggleExpandedLog(log.row)}
                                                                className="px-3 py-1.5 rounded-lg border border-slate-200 text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50"
                                                            >
                                                                {isExpanded ? 'Ocultar' : 'Revisar'}
                                                            </button>
                                                        ) : (
                                                            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-300">Sin accion</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="4" className="px-4 py-4 bg-slate-50/70">
                                                            {log.status === 'pending' && log.context?.kind === 'product_name_conflict' ? (
                                                                <div className="rounded-2xl border border-blue-100 bg-white p-4">
                                                                    <p className="text-[10px] font-black uppercase tracking-widest text-blue-600">Conflicto de nombre</p>
                                                                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                                                                        <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                                                                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-400">Nombre actual</div>
                                                                            <div className="mt-1 text-sm font-black text-slate-700">{log.context.currentName || 'Sin nombre'}</div>
                                                                        </div>
                                                                        <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-3">
                                                                            <div className="text-[9px] font-black uppercase tracking-widest text-blue-500">Nombre importado</div>
                                                                            <div className="mt-1 text-sm font-black text-slate-700">{log.context.importedName || 'Sin nombre'}</div>
                                                                        </div>
                                                                    </div>
                                                                    <div className="mt-4 flex flex-wrap justify-end gap-2">
                                                                        <button
                                                                            onClick={() => resolveNameConflict(log, false)}
                                                                            disabled={isBusy}
                                                                            className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                                                                        >
                                                                            {isBusy && workingAction === 'keep-name' ? 'Guardando...' : 'Mantener actual'}
                                                                        </button>
                                                                        <button
                                                                            onClick={() => resolveNameConflict(log, true)}
                                                                            disabled={isBusy}
                                                                            className="px-4 py-2 rounded-xl bg-blue-600 text-white text-[10px] font-black uppercase tracking-widest hover:bg-blue-700 disabled:opacity-60"
                                                                        >
                                                                            {isBusy && workingAction === 'apply-name' ? 'Guardando...' : 'Usar importado'}
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ) : (
                                                                <div className="rounded-2xl border border-rose-100 bg-white p-4">
                                                                    <div className="flex items-center justify-between gap-3">
                                                                        <div>
                                                                            <p className="text-[10px] font-black uppercase tracking-widest text-rose-600">Corregir fila</p>
                                                                            <p className="mt-1 text-[11px] font-medium text-slate-500">Edita los datos y vuelve a intentar la importacion de esta fila.</p>
                                                                        </div>
                                                                        <button
                                                                            onClick={() => retryImportLog(log)}
                                                                            disabled={isBusy}
                                                                            className="px-4 py-2 rounded-xl bg-[#0B2A36] text-white text-[10px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-60"
                                                                        >
                                                                            {isBusy && workingAction === 'retry' ? 'Reintentando...' : 'Guardar y reintentar'}
                                                                        </button>
                                                                    </div>
                                                                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                                                                        {currentConfig.fields.map((field) => (
                                                                            <label key={`${log.row}-${field.key}`} className="block">
                                                                                <span className="mb-1 block text-[9px] font-black uppercase tracking-widest text-slate-400">{field.label}</span>
                                                                                {field.type === 'boolean' ? (
                                                                                    <select
                                                                                        value={String(log.draft?.[field.key] ?? '')}
                                                                                        onChange={(e) => updateLogDraft(log.row, field.key, e.target.value)}
                                                                                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 outline-none focus:border-blue-400"
                                                                                    >
                                                                                        <option value="">Sin definir</option>
                                                                                        <option value="si">Si</option>
                                                                                        <option value="no">No</option>
                                                                                    </select>
                                                                                ) : (
                                                                                    <input
                                                                                        type="text"
                                                                                        value={String(log.draft?.[field.key] ?? '')}
                                                                                        onChange={(e) => updateLogDraft(log.row, field.key, e.target.value)}
                                                                                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[11px] font-bold text-slate-700 outline-none focus:border-blue-400"
                                                                                        placeholder={field.label}
                                                                                    />
                                                                                )}
                                                                            </label>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                    {filteredLogs.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="px-4 py-8 text-center text-slate-400 text-xs font-bold uppercase">
                                                No hay registros para este filtro.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end">
                        <button
                            data-onboarding-id="import-excel-results-finish"
                            onClick={resetImporter}
                            className="px-6 py-3 bg-[#0B2A36] text-white rounded-xl font-black text-xs uppercase tracking-widest hover:opacity-90"
                        >
                            Finalizar y Volver
                        </button>
                    </div>
                </div>
            )}
        </div>
    </div>
  );
}

export default Importaciones;
